/**
 * Shared delivery-date estimator.
 *
 * This arithmetic used to be copy-pasted into snippets/adc-delivery-date.liquid
 * and snippets/size-selector-drawer.liquid. The copies drifted: the drawer never
 * learned about the extended_delivery metafield, and its holiday table was a
 * hardcoded list that expired at the end of 2025. The two surfaces therefore
 * quoted different dates for the same size. One implementation, two callers.
 *
 *   Torrini.deliveryDate({ days: 14, extended: true })
 *   // => "Fri, 07 Aug 2026"
 *
 * `includeYear: false` drops the year suffix (the drawer's table cells are
 * narrow and keep the short form). `from` overrides "today" so the weekend and
 * holiday skipping can be asserted without mocking the clock — see the
 * verification notes in the delivery-date fix.
 */
(function () {
  'use strict';

  var T = (window.Torrini = window.Torrini || {});

  var EXTENDED_EXTRA_DAYS = 3; // custom.extended_delivery

  var DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  var MONTH_NAMES = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
  ];

  // Build a local-timezone YYYY-MM-DD key (avoids UTC drift near midnight in IT).
  function localISODate(d) {
    var y = d.getFullYear();
    var m = String(d.getMonth() + 1).padStart(2, '0');
    var day = String(d.getDate()).padStart(2, '0');
    return y + '-' + m + '-' + day;
  }

  // Easter Sunday for a given year (Meeus/Jones/Butcher Gregorian algorithm).
  function easterSunday(year) {
    var a = year % 19;
    var b = Math.floor(year / 100);
    var c = year % 100;
    var d = Math.floor(b / 4);
    var e = b % 4;
    var f = Math.floor((b + 8) / 25);
    var g = Math.floor((b - f + 1) / 3);
    var h = (19 * a + b - d - g + 15) % 30;
    var i = Math.floor(c / 4);
    var k = c % 4;
    var l = (32 + 2 * e + 2 * i - h - k) % 7;
    var m = Math.floor((a + 11 * h + 22 * l) / 451);
    var month = Math.floor((h + l - 7 * m + 114) / 31); // 3 = March, 4 = April
    var day = ((h + l - 7 * m + 114) % 31) + 1;
    return new Date(year, month - 1, day);
  }

  // Italian public holidays for a year, generated programmatically (fixed dates +
  // computed Easter Monday) so estimates stay correct for 2026 and beyond.
  var holidayCache = {};
  function getHolidays(year) {
    if (holidayCache[year]) return holidayCache[year];
    var fixed = [
      '01-01', // New Year's Day
      '01-06', // Epiphany
      '04-25', // Liberation Day
      '05-01', // Labor Day
      '06-02', // Republic Day
      '08-15', // Ferragosto
      '11-01', // All Saints' Day
      '12-08', // Immaculate Conception
      '12-25', // Christmas Day
      '12-26', // St. Stephen's Day
    ];
    var set = new Set(
      fixed.map(function (md) {
        return year + '-' + md;
      })
    );
    var easterMonday = easterSunday(year);
    easterMonday.setDate(easterMonday.getDate() + 1); // Easter Monday
    set.add(localISODate(easterMonday));
    holidayCache[year] = set;
    return set;
  }

  function isHoliday(d) {
    return getHolidays(d.getFullYear()).has(localISODate(d));
  }

  function isNonWorkingDay(d) {
    return (
      d.getDay() === 0 || // Sunday
      d.getDay() === 6 || // Saturday
      isHoliday(d)
    );
  }

  // Start from tomorrow, walk forward to the next working day, then consume
  // `daysToAdd` working days. `from` is cloned, never mutated.
  function addBusinessDays(daysToAdd, from) {
    var date = from ? new Date(from.getTime()) : new Date();
    date.setDate(date.getDate() + 1); // Start from the next day

    while (isNonWorkingDay(date)) {
      date.setDate(date.getDate() + 1);
    }

    var remaining = daysToAdd;
    while (remaining > 0) {
      date.setDate(date.getDate() + 1);
      if (isNonWorkingDay(date)) continue;
      remaining--;
    }

    return date;
  }

  function formatDeliveryDate(date, includeYear) {
    var out =
      DAY_NAMES[date.getDay()] +
      ', ' +
      String(date.getDate()).padStart(2, '0') +
      ' ' +
      MONTH_NAMES[date.getMonth()];
    return includeYear ? out + ' ' + date.getFullYear() : out;
  }

  /**
   * opts: { days, extended, includeYear, from }
   * The extended rule lives here, not at the call sites — rules drifting apart
   * between the two surfaces is what caused the mismatch in the first place.
   */
  T.deliveryDate = function (opts) {
    opts = opts || {};
    var days = opts.days;
    if (opts.extended) days += EXTENDED_EXTRA_DAYS;
    return formatDeliveryDate(
      addBusinessDays(days, opts.from),
      opts.includeYear !== false
    );
  };

  // Exposed so the arithmetic can be asserted from the console against a fixed
  // `from` date; the theme itself only calls Torrini.deliveryDate().
  T.deliveryDate.isHoliday = isHoliday;
  T.deliveryDate.addBusinessDays = addBusinessDays;
  T.deliveryDate.format = formatDeliveryDate;
  T.deliveryDate.EXTENDED_EXTRA_DAYS = EXTENDED_EXTRA_DAYS;
})();

/**
 * Torrini section lifecycle.
 *
 * Registers a per-element init function that is (re)run whenever its DOM
 * appears: on first load, on `shopify:section:load` in the theme editor, and
 * on demand after AJAX HTML replacement via Torrini.scan().
 *
 *   Torrini.section('.faq-section', function (root, ctx) {
 *     ctx.on(root, 'click', handler);      // auto-removed on teardown
 *     ctx.interval(tick, 5000);            // auto-cleared
 *     ctx.observe(new ResizeObserver(fn)); // auto-disconnected
 *     ctx.cleanup(function () { ... });    // arbitrary teardown
 *     return function () { ... };          // optional extra teardown
 *   });
 *
 * `root` is the element matched by `selector`, NOT the #shopify-section
 * wrapper, so selectors stay identical to the querySelector calls they replace.
 *
 * Anything acquired through `ctx` is released automatically when the section is
 * removed or reloaded. That is the whole point: without teardown, re-init in
 * the editor duplicates listeners, doubles intervals, and makes
 * noUiSlider.create throw.
 */
(function () {
  'use strict';

  var G = (window.Torrini = window.Torrini || {});

  var entries = []; // [{ selector, key, init }]
  var live = []; // [{ entry, root, ctx }]
  var booted = false;

  function warn(msg, err) {
    if (window.console && console.warn) {
      console.warn('[Torrini] ' + msg, err || '');
    }
  }

  var matchesFn =
    Element.prototype.matches ||
    Element.prototype.msMatchesSelector ||
    Element.prototype.webkitMatchesSelector;

  function matches(el, sel) {
    try {
      return matchesFn.call(el, sel);
    } catch (e) {
      return false;
    }
  }

  // ------------------------------------------------------------------ Ctx --

  function Ctx(root) {
    this.root = root;
    this.destroyed = false;
    this._listeners = [];
    this._timers = [];
    this._rafs = [];
    this._observers = [];
    this._fns = [];
  }

  Ctx.prototype.on = function (target, type, handler, opts) {
    if (!target) return handler; // tolerate optional elements
    target.addEventListener(type, handler, opts);
    this._listeners.push([target, type, handler, opts]);
    return handler;
  };

  Ctx.prototype.interval = function (fn, ms) {
    var id = window.setInterval(fn, ms);
    this._timers.push(['i', id]);
    return id;
  };

  Ctx.prototype.timeout = function (fn, ms) {
    var id = window.setTimeout(fn, ms);
    this._timers.push(['t', id]);
    return id;
  };

  Ctx.prototype.raf = function (fn) {
    var self = this;
    var id = window.requestAnimationFrame(function () {
      if (!self.destroyed) fn();
    });
    this._rafs.push(id);
    return id;
  };

  // Any object with .disconnect(): Mutation/Resize/IntersectionObserver.
  Ctx.prototype.observe = function (observer) {
    this._observers.push(observer);
    return observer;
  };

  Ctx.prototype.cleanup = function (fn) {
    if (typeof fn === 'function') this._fns.push(fn);
    return fn;
  };

  // Guard for promises / fetch callbacks that may resolve after teardown.
  Ctx.prototype.alive = function () {
    return !this.destroyed && document.contains(this.root);
  };

  Ctx.prototype.destroy = function () {
    if (this.destroyed) return;
    this.destroyed = true;

    var i, l;
    for (i = 0; i < this._listeners.length; i += 1) {
      l = this._listeners[i];
      try {
        l[0].removeEventListener(l[1], l[2], l[3]);
      } catch (e) {
        /* target already detached */
      }
    }
    for (i = 0; i < this._timers.length; i += 1) {
      if (this._timers[i][0] === 'i') {
        window.clearInterval(this._timers[i][1]);
      } else {
        window.clearTimeout(this._timers[i][1]);
      }
    }
    for (i = 0; i < this._rafs.length; i += 1) {
      window.cancelAnimationFrame(this._rafs[i]);
    }
    for (i = 0; i < this._observers.length; i += 1) {
      try {
        this._observers[i].disconnect();
      } catch (e) {
        /* ignore */
      }
    }
    // User cleanups run last-registered-first, mirroring setup order. Each is
    // isolated so one throw cannot strand the rest.
    for (i = this._fns.length - 1; i >= 0; i -= 1) {
      try {
        this._fns[i]();
      } catch (e) {
        warn('cleanup threw', e);
      }
    }

    this._listeners = [];
    this._timers = [];
    this._rafs = [];
    this._observers = [];
    this._fns = [];
  };

  // -------------------------------------------------------- mount/unmount --

  function findLive(entry, root) {
    for (var i = 0; i < live.length; i += 1) {
      if (live[i].entry === entry && live[i].root === root) return live[i];
    }
    return null;
  }

  function mount(entry, root) {
    if (findLive(entry, root)) return; // double-init guard
    var ctx = new Ctx(root);
    var rec = { entry: entry, root: root, ctx: ctx };
    live.push(rec); // push BEFORE init so a re-entrant scan is guarded
    try {
      var teardown = entry.init(root, ctx);
      if (typeof teardown === 'function') ctx.cleanup(teardown);
    } catch (e) {
      // Contain the failure: one broken section must not stop the others.
      warn('init failed for "' + entry.selector + '"', e);
    }
  }

  function unmount(rec) {
    var i = live.indexOf(rec);
    if (i > -1) live.splice(i, 1);
    rec.ctx.destroy();
  }

  function mountEntryIn(entry, scope) {
    if (scope.nodeType === 1 && matches(scope, entry.selector)) {
      mount(entry, scope);
    }
    var found = scope.querySelectorAll(entry.selector);
    for (var j = 0; j < found.length; j += 1) mount(entry, found[j]);
  }

  function scan(scope) {
    scope = scope || document;
    for (var i = 0; i < entries.length; i += 1) mountEntryIn(entries[i], scope);
  }

  function destroyIn(scope) {
    for (var i = live.length - 1; i >= 0; i -= 1) {
      if (live[i].root === scope || (scope.contains && scope.contains(live[i].root))) {
        unmount(live[i]);
      }
    }
  }

  // Safety net: Shopify does not guarantee `unload` fires before `load`, and
  // AJAX innerHTML replacement fires nothing at all. Anything whose root has
  // left the document is dead, so reclaim it.
  function sweepDetached() {
    for (var i = live.length - 1; i >= 0; i -= 1) {
      if (!document.contains(live[i].root)) unmount(live[i]);
    }
  }

  // --------------------------------------------------------------- public --

  // Registration identity is the pair (selector, key).
  //
  // Dedupe defaults to the selector alone: when the editor re-executes a
  // section's inline script, that module's re-registration must REPLACE its
  // previous handler rather than accumulate. The re-executed script yields a
  // new function object whose source can also legitimately differ (init bodies
  // embed Liquid -- see sections/product-details.liquid), so neither reference
  // nor source identity can serve as the test.
  //
  // The cost is that two DIFFERENT modules targeting the same element would
  // collapse into one, silently discarding the earlier init. Pass a third
  // `key` to keep them apart. It must be a literal constant, stable across
  // re-renders:
  //
  //   Torrini.section('#size-guide-drawer', init);             // drawer UI
  //   Torrini.section('#size-guide-drawer', init, 'delivery'); // delivery dates
  //   Torrini.section('#size-guide-drawer', init, 'a11y');     // dialog semantics
  G.section = function (selector, initFn, key) {
    if (typeof initFn !== 'function') return;
    key = key || '';
    for (var i = 0; i < entries.length; i += 1) {
      if (entries[i].selector === selector && entries[i].key === key) {
        // Before boot every registration is a first registration, so a
        // collision here is always two modules fighting over one selector,
        // never an editor reload. Surface it instead of losing an init.
        if (!booted) {
          warn(
            'duplicate registration for "' + selector + '"; the earlier init ' +
            'will be discarded. Pass a distinct third `key` argument if these ' +
            'are different modules.'
          );
        }
        entries[i].init = initFn;
        return;
      }
    }
    var entry = { selector: selector, key: key, init: initFn };
    entries.push(entry);
    if (booted) mountEntryIn(entry, document); // registered late
  };

  // For AJAX-injected HTML, where no Shopify event fires. Call destroy()
  // before replacing innerHTML and scan() after.
  G.scan = function (scope) {
    sweepDetached();
    scan(scope || document);
  };

  G.destroy = function (scope) {
    destroyIn(scope || document);
  };

  // Debug aid: Torrini._live.length should return to baseline after an
  // add -> remove cycle in the editor.
  G._live = live;

  // ----------------------------------------------------------------- boot --

  var queued = G.q || [];
  G.q = [];
  for (var qi = 0; qi < queued.length; qi += 1) {
    G.section(queued[qi][0], queued[qi][1], queued[qi][2]);
  }

  function boot() {
    if (booted) return;
    booted = true;
    scan(document);
  }

  document.addEventListener('shopify:section:load', function (e) {
    sweepDetached(); // reclaim the replaced subtree
    if (!booted) return;
    scan(e.target);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    destroyIn(e.target);
  });

  /**
   * Theme-editor block selection.
   *
   * The lifecycle half of the editor contract (section load / unload) was
   * handled; the selection half was not. When a merchant clicks a block in the
   * sidebar, blocks that sit inside a collapsed accordion, an inactive tab or
   * a closed dropdown were simply not visible, and nothing appeared to happen.
   *
   * `event.target` is the block's own DOM element - the one carrying
   * {{ block.shopify_attributes }} - so there is nothing to look up.
   *
   * Every case below is driven by clicking the control the component already
   * owns, rather than reaching into its state. That keeps this handler out of
   * three sections' internals: whatever the accordion does on a real click, it
   * does here too, including any bookkeeping this file cannot see. The guards
   * stop a click from *closing* something already open.
   */
  document.addEventListener('shopify:block:select', function (e) {
    var block = e.target;
    if (!block || !block.closest) return;

    // 1. The block is its own control (customer-service-tabs' <button role="tab">).
    if (block.getAttribute('role') === 'tab' && block.getAttribute('aria-selected') !== 'true') {
      block.click();
    }

    // 2. The block contains its control (faq-section's toggle, megamenu's
    //    dropdown button). aria-expanded is the shared "is it open" signal.
    var toggle = block.querySelector('[data-faq-toggle], .megamenu__toggle');
    if (toggle && toggle.getAttribute('aria-expanded') !== 'true') {
      toggle.click();
    }

    // 3. Everything else (footer menus, plain grids) just needs to be on
    //    screen. Harmless for the cases above, which have already been
    //    revealed by the time this runs.
    if (typeof block.scrollIntoView === 'function') {
      block.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  });

  // Close what block:select opened, so the editor does not accumulate open
  // dropdowns as the merchant clicks through blocks. Only the megamenu:
  // collapsing an accordion or switching a tab back would be more disruptive
  // than leaving it, and there is no "previous" state to restore to.
  document.addEventListener('shopify:block:deselect', function (e) {
    var block = e.target;
    if (!block || !block.querySelector) return;

    var toggle = block.querySelector('.megamenu__toggle');
    if (toggle && toggle.getAttribute('aria-expanded') === 'true') {
      toggle.click();
    }
  });

  if (document.readyState === 'complete') {
    boot();
  } else {
    // Covers 'loading' and 'interactive'. application.js is deferred so it
    // normally runs at 'interactive'; booting on DOMContentLoaded preserves
    // today's timing.
    document.addEventListener('DOMContentLoaded', boot);
  }
})();

/**
 * Delegated handlers for markup that used to carry inline on* attributes.
 *
 * The theme had nine inline handlers (onclick/onmouseover) calling bare global
 * functions: openMiniCart, closeMiniCart, openWishlist, closeWishlist,
 * updateQuantity, closeMobileMenu, showOverlay, hideOverlay. Those globals only
 * existed because top-level `function` declarations in classic scripts land on
 * `window`; moving any of that code into a scoped init would have silently
 * turned the attributes into no-ops — a dead cart button with no console error.
 *
 * These listeners are delegated from `document` and bound exactly once, so they
 * survive both section reloads and the AJAX re-renders that replace mini-cart
 * markup wholesale (where inline handlers previously only survived by luck).
 *
 * The same reasoning makes this the right home for behaviour belonging to
 * snippets rendered inside sections (the email input's clear button): a script
 * in the snippet would be re-emitted on every render.
 */
(function () {
  'use strict';

  var T = (window.Torrini = window.Torrini || {});

  /**
   * Announce a message to assistive technology.
   *
   * Writes into the single #a11y-announcer live region in layout/theme.liquid.
   * Use for state changes that are visual-only - added to cart, quantity
   * changed, N products match the filter, saved to wishlist - where nothing
   * reloads and nothing takes focus, so a screen reader would otherwise get
   * no signal at all.
   *
   * The clear-then-write on a timeout is not superstition: setting textContent
   * to the value it already holds is not a DOM mutation, so announcing the
   * same message twice in a row (add the same item to the cart again) would be
   * silent the second time. Clearing first guarantees a change to observe.
   *
   * No-ops if the region is absent - layout/password.liquid does not render it.
   */
  T.announce = function (message) {
    if (!message) return;
    var region = document.getElementById('a11y-announcer');
    if (!region) return;

    region.textContent = '';
    window.setTimeout(function () {
      region.textContent = message;
    }, 100);
  };

  document.addEventListener('click', function (e) {
    // --- drawer triggers: data-drawer-open="minicart|wishlist" -------------
    var opener = e.target.closest ? e.target.closest('[data-drawer-open]') : null;
    if (opener) {
      e.preventDefault();
      var which = opener.getAttribute('data-drawer-open');

      if (opener.hasAttribute('data-close-mobile-menu') && typeof T.closeMobileMenu === 'function') {
        T.closeMobileMenu();
        // Let the mobile menu finish closing before the drawer slides in.
        window.setTimeout(function () { openDrawer(which); }, 300);
        return;
      }
      openDrawer(which);
      return;
    }

    // --- mini-cart quantity: data-cart-key + data-cart-quantity -----------
    var qtyBtn = e.target.closest ? e.target.closest('[data-cart-key]') : null;
    if (qtyBtn && typeof T.updateQuantity === 'function') {
      e.preventDefault();
      T.updateQuantity(
        qtyBtn.getAttribute('data-cart-key'),
        parseInt(qtyBtn.getAttribute('data-cart-quantity'), 10),
        qtyBtn
      );
      return;
    }

    // --- cookie preferences: data-cookie-preferences -----------------------
    // Re-opens Shopify's consent modal. The control is rendered in two places
    // (the footer and the mobile drawer's policy links), so the behaviour lives
    // here rather than in either one. The typeof guard is defensive only — the
    // readiness poll below keeps the control hidden until the API is really
    // there — but a merchant can switch the banner off mid-session.
    var cookieBtn = e.target.closest ? e.target.closest('[data-cookie-preferences]') : null;
    if (cookieBtn) {
      e.preventDefault();
      if (window.privacyBanner && typeof window.privacyBanner.showPreferences === 'function') {
        window.privacyBanner.showPreferences();
      }
      return;
    }

    // --- email input clear button: snippets/input.liquid -------------------
    var clearBtn = e.target.closest ? e.target.closest('.clear-button') : null;
    if (clearBtn) {
      var wrapper = clearBtn.closest('.input-wrapper');
      var field = wrapper && wrapper.querySelector('.input-field');
      if (field) {
        field.value = '';
        toggleClearButton(field);
        field.focus();
      }
    }
  });

  // The clear button is display:none until the field has something to clear.
  // Delegated from document rather than bound per-input because
  // snippets/input.liquid renders inside sections, so a script in the snippet
  // would be re-emitted on every render.
  document.addEventListener('input', function (e) {
    if (e.target && e.target.classList && e.target.classList.contains('input-field')) {
      toggleClearButton(e.target);
    }
  });

  function toggleClearButton(field) {
    var wrapper = field.closest('.input-wrapper');
    var btn = wrapper && wrapper.querySelector('.clear-button');
    if (btn) btn.classList.toggle('clear-button--visible', field.value.length > 0);
  }

  /**
   * Reveal the "Manage cookies" controls once Shopify's consent banner is there.
   *
   * `window.privacyBanner` is injected asynchronously through
   * {{ content_for_header }}, and only when the merchant has switched the cookie
   * banner on in Settings -> Customer privacy. There is no theme-side way to
   * create it. Until it exists, showPreferences() has nothing to open, so the
   * controls stay `display: none` and the storefront shows no dead affordance.
   *
   * The signal is a class on <html>, not an attribute flipped on each button:
   * the footer and the mobile drawer are both re-rendered (theme editor,
   * AJAX), and a root class covers markup that appears later for free. Bound
   * once at document level, so there is nothing to tear down.
   */
  var COOKIE_POLL_MS = 500;
  var COOKIE_POLL_LIMIT = 20; // ~10s, then stop: the banner is simply switched off.

  (function watchForPrivacyBanner() {
    var tries = 0;

    function ready() {
      return !!(window.privacyBanner && typeof window.privacyBanner.showPreferences === 'function');
    }

    function reveal() {
      document.documentElement.classList.add('cookie-prefs-ready');
    }

    if (ready()) {
      reveal();
      return;
    }

    var timer = window.setInterval(function () {
      tries += 1;
      if (ready()) {
        reveal();
        window.clearInterval(timer);
      } else if (tries >= COOKIE_POLL_LIMIT) {
        window.clearInterval(timer);
      }
    }, COOKIE_POLL_MS);
  })();

  function openDrawer(which) {
    if (which === 'minicart' && typeof T.openMiniCart === 'function') {
      T.openMiniCart();
    } else if (which === 'wishlist' && typeof T.openWishlist === 'function') {
      T.openWishlist();
    }
  }
})();

/**
 * Collection sorting.
 *
 * Moved here from snippets/sorting-filter.liquid, which is rendered TWICE per
 * collection page (snippets/filtering.liquid:12 for the mobile bar and :87 for
 * the desktop rail). That snippet declared `let originalProductOrder` at the
 * top level of a classic script, so the browser threw
 *   "Identifier 'originalProductOrder' has already been declared"
 * on the second copy and skipped it entirely. The page only worked because the
 * first copy had already defined everything, but the error was logged on every
 * collection page load.
 *
 * Living here, the state is declared once in module scope and each sorting
 * dropdown is mounted individually.
 */
(function () {
  'use strict';

  // Captured once, lazily, so "Featured" can restore the server-rendered order.
  var originalProductOrder = [];

  function captureOriginalOrder() {
    if (originalProductOrder.length) return;
    var container = document.querySelector('.products-section__grid');
    if (!container) return;
    originalProductOrder = Array.prototype.slice.call(
      document.querySelectorAll('.products-section__item')
    );
  }

  function updateSortingLabel(dropdown) {
    var activeSort = dropdown.querySelector(
      '[data-filter-type="sorting"].filtering__dropdown-item--active'
    );
    if (!activeSort) return;
    var toggleLabel = dropdown.querySelector('.filtering__dropdown-label');
    if (!toggleLabel) return;
    toggleLabel.textContent = activeSort.textContent.trim();
    if (!toggleLabel.getAttribute('data-original-text')) {
      toggleLabel.setAttribute('data-original-text', 'Sort By');
    }
  }

  function applySorting(sortValue) {
    var productsContainer = document.querySelector('.products-section__grid');
    if (!productsContainer) return;

    var currentProductItems = Array.prototype.slice.call(
      document.querySelectorAll('.products-section__item')
    );
    var productsToSort = currentProductItems.slice();

    if (sortValue === 'default') {
      if (originalProductOrder.length > 0) {
        productsToSort = originalProductOrder.slice();
      }
    } else {
      var priceData = [];
      if (sortValue.indexOf('price') > -1) {
        var priceDataElement = document.getElementById('product-price-data');
        if (priceDataElement) {
          try {
            priceData = JSON.parse(priceDataElement.textContent);
          } catch (error) {
            console.error('Error parsing product price data:', error);
          }
        }
      }

      productsToSort.sort(function (a, b) {
        var cardA = a.querySelector('.product-card');
        var cardB = b.querySelector('.product-card');
        if (!cardA || !cardB) return 0;

        var titleElA = cardA.querySelector('.product-card__title');
        var titleElB = cardB.querySelector('.product-card__title');
        var titleA = titleElA ? titleElA.textContent.trim() : '';
        var titleB = titleElB ? titleElB.textContent.trim() : '';

        var idA = cardA.getAttribute('data-product-id');
        var idB = cardB.getAttribute('data-product-id');

        function priceFor(id) {
          for (var i = 0; i < priceData.length; i += 1) {
            /* eslint-disable-next-line eqeqeq */
            if (priceData[i].id == id) return parseFloat(priceData[i].price) / 100;
          }
          return 0;
        }
        var priceA = priceFor(idA);
        var priceB = priceFor(idB);

        switch (sortValue) {
          case 'alpha-asc': return titleA.localeCompare(titleB);
          case 'alpha-desc': return titleB.localeCompare(titleA);
          case 'price-asc': return priceA - priceB;
          case 'price-desc': return priceB - priceA;
          default: return 0;
        }
      });
    }

    currentProductItems.forEach(function (item) { item.remove(); });

    productsToSort.forEach(function (item, index) {
      item.classList.add('product-sorting');
      productsContainer.appendChild(item);
      window.setTimeout(function () {
        item.classList.remove('product-sorting');
      }, index * 50);
    });
  }

  Torrini.section('.filtering__dropdown--sorting', function (dropdown, ctx) {
    captureOriginalOrder();

    var sortingItems = dropdown.querySelectorAll('[data-sort-value]');
    Array.prototype.forEach.call(sortingItems, function (item) {
      ctx.on(item, 'click', function (e) {
        e.preventDefault();
        e.stopPropagation();

        // Active state is synced across BOTH sorting dropdowns (mobile bar and
        // desktop rail), so they cannot drift apart.
        var allSortItems = document.querySelectorAll('[data-filter-type="sorting"]');
        Array.prototype.forEach.call(allSortItems, function (el) {
          el.classList.remove('filtering__dropdown-item--active');
        });

        var value = item.getAttribute('data-sort-value');
        var matching = document.querySelectorAll('[data-sort-value="' + value + '"]');
        Array.prototype.forEach.call(matching, function (el) {
          el.classList.add('filtering__dropdown-item--active');
        });

        var content = item.closest('.filtering__dropdown-content');
        var parent = item.closest('.filtering__dropdown');
        if (content) content.classList.remove('filtering__dropdown-content--active');
        if (parent) {
          var toggle = parent.querySelector('.filtering__dropdown-toggle');
          if (toggle) toggle.classList.remove('filtering__dropdown-toggle--active');
        }

        // Refresh every sorting dropdown's label, not just this one.
        Array.prototype.forEach.call(
          document.querySelectorAll('.filtering__dropdown--sorting'),
          updateSortingLabel
        );

        applySorting(value);
      });
    });

    // Make sure the label matches the active selection on load.
    updateSortingLabel(dropdown);
  });

  Torrini.applySorting = applySorting;

  /**
   * Folds a "Load more" page into the client-side sort/filter state.
   * Called by sections/products-section.liquid after it appends the new cards.
   *
   * Two things break without it:
   *
   * 1. applySorting() clears the grid and re-appends only `originalProductOrder`
   *    when the sort is "default", and that array is captured once on mount.
   *    So appended products disappeared outright the moment a shopper switched
   *    the sort back to "Featured".
   * 2. Both the sorter and snippets/filtering.liquid's applyFilter() read prices
   *    from #product-price-data, which the server renders for the current page
   *    only. Appended products therefore sorted as price 0 and passed straight
   *    through any active price range.
   *
   * This does not fix H2 — filter *options* are still built from page 1, and no
   * filter state lives in the URL. Only the native Search & Discovery migration
   * fixes that.
   */
  Torrini.registerLoadedProducts = function (items, priceJson) {
    if (originalProductOrder.length) {
      Array.prototype.push.apply(originalProductOrder, items);
    } else {
      // Nothing captured yet (no sorting dropdown on this template), so the
      // grid as it now stands — including `items` — is the server order.
      captureOriginalOrder();
    }
    mergePriceData(priceJson);
  };

  function mergePriceData(priceJson) {
    if (!priceJson) return;
    var el = document.getElementById('product-price-data');
    if (!el) return;
    try {
      var merged = JSON.parse(el.textContent);
      var seen = {};
      merged.forEach(function (p) { seen[p.id] = true; });
      JSON.parse(priceJson).forEach(function (p) {
        if (!seen[p.id]) merged.push(p);
      });
      el.textContent = JSON.stringify(merged);
    } catch (error) {
      console.error('Error merging product price data:', error);
    }
  }
})();

/**
 * Product card — material pills.
 *
 * Moved here from snippets/product-card.liquid. That snippet renders inside
 * {% for %} loops at six call sites, so it previously emitted this entire
 * script once per card: on a 24-product grid that meant 24 copies of the
 * source, 24 reassignments of the init function, 24 DOMContentLoaded
 * listeners, and — because the tooltip's dismiss handler was registered
 * *inside* the init function — 24 document-level click listeners, each with
 * its own activeTooltip closure.
 *
 * Here the tooltip is a module-level singleton whose document listener is
 * bound exactly once, and each card is mounted individually by the lifecycle
 * helper (which also replaces the old [data-materials-init] guard).
 */
(function () {
  'use strict';

  var activeTooltip = null;

  function removeTooltip(animate) {
    if (!activeTooltip) return;
    var el = activeTooltip;
    activeTooltip = null;
    if (!animate) {
      el.remove();
      return;
    }
    el.classList.remove('material-tooltip--visible');
    var onDone = function () { el.remove(); };
    el.addEventListener('transitionend', onDone, { once: true });
    window.setTimeout(onDone, 250);
  }

  // Bound once, ever — not per card, and never torn down.
  document.addEventListener('click', function () {
    removeTooltip(false);
  });

  function selectDefaultMaterial(productCard) {
    var pills = productCard.querySelectorAll('.product-card__material-pill');

    var currentPill = null;
    var firstPill = null;

    Array.prototype.forEach.call(pills, function (pill) {
      if (!firstPill) firstPill = pill;

      if (pill.getAttribute('data-is-current') === 'true') {
        currentPill = pill;
      }
    });

    var selectedPill = currentPill || firstPill;

    if (selectedPill) {
      selectedPill.click();
    }
  }

  Torrini.section('.product-card', function (productCard, ctx) {
    var materialPills = productCard.querySelectorAll('.product-card__material-pill');
    if (!materialPills.length) return;

    var mainImage = productCard.querySelector('.product-card__main-image');
    var mainLink = productCard.querySelector('.product-card__main-link');
    var priceWrapper = productCard.querySelector('.product-card__price-wrapper');
    var titleElement = productCard.querySelector('.product-card__title');
    if (!mainImage || !mainLink) return;

    var originalUrl = mainLink.getAttribute('data-original-url');

    Array.prototype.forEach.call(materialPills, function (pill) {
      var productHandle = pill.getAttribute('data-product-handle');
      var isCurrentProduct = pill.getAttribute('data-is-current') === 'true';

      ctx.on(pill, 'click', function (event) {
        event.preventDefault();
        event.stopPropagation();

        var cardPills = productCard.querySelectorAll('.product-card__material-pill');
        Array.prototype.forEach.call(cardPills, function (p) {
          p.classList.remove('product-card__material-pill--active');
          p.classList.remove('product-card__material-pill--current');
        });

        pill.classList.add('product-card__material-pill--active');

        // srcset wins: once an <img> carries one, the browser picks its source
        // from srcset/sizes and ignores src entirely. SA10 gave the card image a
        // srcset, which silently turned the src-only swap below into a no-op, so
        // both attributes have to move together. The placeholder branch of
        // product-card.liquid emits an <svg>, which has no src to set.
        var productImageUrl = pill.getAttribute('data-product-image');
        if (productImageUrl && mainImage.tagName === 'IMG') {
          mainImage.srcset = pill.getAttribute('data-product-srcset') || '';
          mainImage.src = productImageUrl;
        }

        if (isCurrentProduct) {
          mainLink.href = originalUrl;
        } else {
          mainLink.href = '/products/' + productHandle;
        }

        var productPrice = pill.getAttribute('data-product-price');
        var compareAtPrice = pill.getAttribute('data-compare-at-price');
        if (priceWrapper && productPrice) {
          if (compareAtPrice) {
            priceWrapper.innerHTML =
              '<span class="product-card__price--sale">' + productPrice + '</span>' +
              '<span class="product-card__price--compare">' + compareAtPrice + '</span>';
          } else {
            priceWrapper.innerHTML = '<span class="product-card__price">' + productPrice + '</span>';
          }
        }

        var productTitle = pill.getAttribute('data-product-title');
        if (productTitle) {
          if (titleElement) titleElement.textContent = productTitle;
          if (mainImage.tagName === 'IMG') mainImage.alt = productTitle;
        }
      });

      ctx.on(pill, 'mouseenter', function () {
        removeTooltip(false);

        var tooltip = document.createElement('div');
        tooltip.className = 'material-tooltip';
        tooltip.textContent = pill.getAttribute('title');
        document.body.appendChild(tooltip);

        var pillRect = pill.getBoundingClientRect();
        tooltip.style.left =
          (pillRect.left + (pillRect.width / 2) - (tooltip.offsetWidth / 2)) + 'px';
        tooltip.style.top = (pillRect.top - tooltip.offsetHeight - 5) + 'px';

        ctx.raf(function () {
          tooltip.classList.add('material-tooltip--visible');
        });

        activeTooltip = tooltip;
      });

      ctx.on(pill, 'mouseleave', function () {
        removeTooltip(true);
      });
    });

    // The tooltip is appended to <body>, so it outlives the card unless we
    // drop it explicitly when the card goes away.
    ctx.cleanup(function () {
      removeTooltip(false);
    });

    if (productCard.querySelector('.product-card__material-options')) {
      selectDefaultMaterial(productCard);
    }
  });

  // Back-compat shim for the AJAX call sites that still call this by name.
  // Torrini.scan() is the correct entry point; this delegates to it.
  window.initProductCardMaterials = function (container) {
    Torrini.scan(container || document);
  };
})();

/**
 * Drawer / dialog accessibility helper.
 *
 * Every modal drawer in the theme is opened/closed by its own inline script,
 * which simply toggles an "active" class on the drawer root (see each snippet's
 * open / close functions). This module layers WCAG dialog semantics on top of
 * that existing behaviour WITHOUT touching those scripts: it watches the class
 * via a MutationObserver and, on open, moves focus into the drawer, traps Tab,
 * closes on Escape, makes the rest of the page inert, and restores focus to the
 * triggering element on close.
 *
 * To register a new drawer, add an entry to DRAWERS below.
 */
(function () {
  'use strict';

  // Each modal drawer: its root element id and the class its inline script
  // toggles when open.
  var DRAWERS = [
    { id: 'minicartOverlay', activeClass: 'is-active' },
    { id: 'wishlistOverlay', activeClass: 'is-active' },
    { id: 'filter-drawer', activeClass: 'is-active' },
    { id: 'size-guide-drawer', activeClass: 'is-active' },
    { id: 'details-drawer', activeClass: 'is-active' },
    { id: 'mobileMenuOverlay', activeClass: 'active' }
  ];

  var FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  var labelCounter = 0;

  function getFocusable(container) {
    return Array.prototype.filter.call(
      container.querySelectorAll(FOCUSABLE),
      function (el) {
        // Skip elements that are not currently visible / laid out.
        return el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement;
      }
    );
  }

  function DrawerA11y(root, activeClass) {
    this.root = root;
    this.activeClass = activeClass;
    this.isOpen = root.classList.contains(activeClass);
    this.trigger = null;
    this.inerted = [];

    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    if (!root.hasAttribute('tabindex')) {
      root.setAttribute('tabindex', '-1');
    }

    // Associate the drawer's heading as its accessible name.
    var heading = root.querySelector('h2, h1, [data-drawer-title]');
    if (heading) {
      if (!heading.id) {
        heading.id = 'drawer-title-' + (labelCounter += 1);
      }
      root.setAttribute('aria-labelledby', heading.id);
    }

    // The element Escape "clicks" to close, reusing the drawer's own close logic.
    this.closeEl =
      root.querySelector('[data-drawer-close]') ||
      root.querySelector('[class*="close"]');

    this.onKeydown = this.onKeydown.bind(this);

    var self = this;
    this.observer = new MutationObserver(function () {
      var nowOpen = root.classList.contains(activeClass);
      if (nowOpen === self.isOpen) return;
      self.isOpen = nowOpen;
      if (nowOpen) {
        self.activate();
      } else {
        self.deactivate();
      }
    });
    this.observer.observe(root, { attributes: true, attributeFilter: ['class'] });
  }

  DrawerA11y.prototype.activate = function () {
    this.trigger =
      document.activeElement && document.activeElement !== document.body
        ? document.activeElement
        : null;

    this.applyInert();
    document.addEventListener('keydown', this.onKeydown, true);

    // Move focus into the drawer.
    var self = this;
    var target =
      this.root.querySelector('[autofocus]') || this.closeEl || this.root;
    // Defer so the drawer is painted / display:block has applied.
    window.requestAnimationFrame(function () {
      try {
        target.focus({ preventScroll: true });
      } catch (e) {
        self.root.focus();
      }
    });
  };

  DrawerA11y.prototype.deactivate = function () {
    document.removeEventListener('keydown', this.onKeydown, true);
    this.releaseInert();
    if (this.trigger && document.contains(this.trigger)) {
      try {
        this.trigger.focus({ preventScroll: true });
      } catch (e) {
        /* ignore */
      }
    }
    this.trigger = null;
  };

  DrawerA11y.prototype.onKeydown = function (e) {
    if (e.key === 'Escape' || e.key === 'Esc') {
      e.preventDefault();
      if (this.closeEl) {
        this.closeEl.click();
      } else {
        this.root.classList.remove(this.activeClass);
      }
      return;
    }

    if (e.key !== 'Tab') return;

    var focusable = getFocusable(this.root);
    if (!focusable.length) {
      e.preventDefault();
      this.root.focus();
      return;
    }
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    var active = document.activeElement;

    if (e.shiftKey) {
      if (active === first || !this.root.contains(active)) {
        e.preventDefault();
        last.focus();
      }
    } else if (active === last) {
      e.preventDefault();
      first.focus();
    }
  };

  // Make every element that is NOT an ancestor of the drawer inert, by walking
  // up the tree and inerting the drawer's siblings at each level.
  DrawerA11y.prototype.applyInert = function () {
    var node = this.root;
    while (node && node.parentElement && node !== document.body) {
      var parent = node.parentElement;
      var children = parent.children;
      for (var i = 0; i < children.length; i += 1) {
        var sib = children[i];
        if (sib !== node && !sib.hasAttribute('inert')) {
          sib.setAttribute('inert', '');
          sib.setAttribute('aria-hidden', 'true');
          this.inerted.push(sib);
        }
      }
      node = parent;
    }
  };

  DrawerA11y.prototype.releaseInert = function () {
    for (var i = 0; i < this.inerted.length; i += 1) {
      this.inerted[i].removeAttribute('inert');
      this.inerted[i].removeAttribute('aria-hidden');
    }
    this.inerted = [];
  };

  DrawerA11y.prototype.destroy = function () {
    this.observer.disconnect();
    document.removeEventListener('keydown', this.onKeydown, true);
    this.releaseInert();
    this.trigger = null;
  };

  // Registered through the lifecycle helper rather than a one-shot
  // DOMContentLoaded pass. Every drawer root is rendered by a section (header
  // renders the mini-cart, wishlist and mobile nav; product-details renders the
  // size guide and details drawers; products-section renders the filter
  // drawer), so an editor re-render used to leave this module observing a
  // detached node while the new drawer had no dialog semantics at all.
  for (var d = 0; d < DRAWERS.length; d += 1) {
    (function (drawer) {
      // Keyed: every one of these roots is also claimed by the drawer's own
      // snippet init. Without the key this registration would replace it.
      Torrini.section('#' + drawer.id, function (root, ctx) {
        var instance = new DrawerA11y(root, drawer.activeClass);
        ctx.cleanup(function () {
          instance.destroy();
        });
      }, 'a11y');
    })(DRAWERS[d]);
  }
})();
